import { useMutation, useQuery } from "@apollo/client";
import { Card, Grid } from "@mui/material";
import { useEffect, useState } from "react";
import { useParams } from "react-router";

import { EditPersonMutation, EditPersonMutationVariables, GetRolesQuery } from "../../__generated__/graphql";
import { GET_ROLES } from "../../features/graphql/query/roles/getRoles";
import LinkItemsHeader from "../Manage/components/ManageDetails/components/LinkItems/LinkItemsHeader";
import { PersonDataType, useSelectedPerson } from "../../context/SelectedPersonContext";
import { ApiValidationError, FormSubmitCallback, mapApiFieldValidationErrors } from "../../components/Form";
import { EDIT_PERSON_MUTATION } from "../../features/graphql/mutation/person/editPerson";
import Header from "../Manage/components/Header/Header";
import { useManageModal } from "../Manage/components/ManageModal";
import StaffForm, { StaffFormSchema, StaffRole } from "./components/StaffForm";
import PersonTableLinkPreview from "./components/StaffTableLinkPreview";
import usePersonStudentAssignment from "./hooks/usePersonStudentAssignment";
import usePersonAuthStore, { UserRoleHierarchy } from "../store/personAuthStore"

import { useExpirePerson } from '../Manage/hooks/useExpirePerson';
import StaffLocationTable from "./components/StaffLocationTable";

interface ManageDetailsProps {
  onBack: () => void,
  onLink: (id: string) => void,
}

type RouteParams = {
  pid: string
}

export default function PersonManagePage({
  onBack,
  onLink,
}: ManageDetailsProps) {

  const params = useParams<RouteParams>();

  const { person, setSelectedPerson } = useSelectedPerson(params.pid);

  const { expirePerson } = useExpirePerson();

  const { openModal, closeModal } = useManageModal();

  const { save } = usePersonStudentAssignment(params.pid ?? '');

  const { loading, data: roleDocument } = useQuery<GetRolesQuery>(GET_ROLES);

  const [editPerson] = useMutation<EditPersonMutation, EditPersonMutationVariables>(EDIT_PERSON_MUTATION);

  const [personRoles, setPersonRoles] = useState<StaffRole[] | null | undefined>([]);

  const [removeRowId, setRemoveRowId] = useState<string | null>(null);
  const roles = usePersonAuthStore(state => state.roles)
  const myHighestRole = usePersonAuthStore((state) => state.highestRole)

  useEffect(() => {
    const roles = roleDocument?.roles.map((role): StaffRole => ({
      name: role.name,
      value: role.normalizedName,
      default: role.default
    })).filter((x) => x.name !== "Super Admin");

    setPersonRoles(roles)
  }, [loading, roleDocument]);


  const handleEditPersonSubmission: FormSubmitCallback<StaffFormSchema> = async (values):
    Promise<ReturnType<FormSubmitCallback>> => {

    const response = await editPerson({
      variables: {
        input: {
          id: person?.id,
          firstName: values.firstName,
          middleName: values.middleName,
          lastName: values.lastName,
          email: values.email,
          jobTitle: values.jobTitle,
          userRole: values.roleType,
          startDate: person?.startDate
        }
      }
    });

    if (response.data?.editPerson.errors) {

      const validationError = response.data.editPerson.errors
        .find(e => e.__typename === "ValidationError") as ApiValidationError;

      const fieldErrors = validationError
        ? mapApiFieldValidationErrors(validationError)
        : null;

      return fieldErrors;
    }

    const replace = {
      ...person,
      // SO dirty. Essientially it's the same data, but the type is different.
      // People should look into better, shared type management with fragment
      ...response.data?.editPerson.person
    } as PersonDataType

    setSelectedPerson(replace);
    openModal({ type: "SAVED_CHANGES", cancelButtonLabel: "Close" })

    // console.log('Submitting the details form', values);
  }

  const handleExpire = async () => {
    const role = person?.role?.normalizedName ?? "";
    const userDoesNotHaveAccess = UserRoleHierarchy[myHighestRole as keyof typeof UserRoleHierarchy] < UserRoleHierarchy[role as keyof typeof UserRoleHierarchy]
    if (roles.includes("SUPPORT ADMIN")) {
      openModal({
        type: 'CANNOT_ARCHIVE_HIGHER_USER',
        cancelButtonLabel: "Close"
      })
    }
    else if (!userDoesNotHaveAccess) {
      openModal({
        type: 'CANNOT_ARCHIVE_HIGHER_USER',
        cancelButtonLabel: "Close"
      })
    } else {
      if (!person?.expired) {
        openModal({
          type: 'EXPIRE_PERSON',
          confirmButtonType: 'WARNING',
          confirmButtonLabel: 'Expire',
          onConfirm: async () => {
            openModal(state => ({
              ...state,
              status: 'SAVING'
            }))

            await expirePerson(person?.id)

            closeModal();
          }
        })
      } else {
        openModal({
          type: 'REACTIVATE',
        })
      }
    }
  }

  const handleUnassignment = async (id: string) => {
    setRemoveRowId(id);
    openModal({
      type: 'REMOVE_ASSIGNMENT',
      confirmButtonType: 'WARNING',
      confirmButtonLabel: 'Yes',
      onConfirm: async () => {
        openModal(state => ({
          ...state,
          status: 'SAVING'
        }));

        const unassign = [id];
        await save([], unassign);

        setRemoveRowId(null);

        closeModal();
      },
      onCancel: () => {
        setRemoveRowId(null);
      },
    })
  }

  return (
    <Card
      elevation={4}
      style={{
        width: "100%",
        display: "flex",
        padding: "16px",
        height: "auto",
      }}
    >
      <Grid container spacing={2}>
        <Grid size={12} padding={0}>
          <Grid container spacing={0}>
            <Grid size={6} >
              <Header
                onBack={onBack}
                title={person
                  ? `Details for ${person?.firstName} ${person?.lastName}`
                  : 'Loading...'}
              />
            </Grid>
            <Grid size={6}>
              <LinkItemsHeader
                entityId={person?.id ?? ''}
                startDate={person?.startDate}
                endDate={person?.endDate}
                expired={person?.expired}
                onExpire={handleExpire} />
            </Grid>
          </Grid>
        </Grid>
        <Grid size={12}>
          <Grid container spacing={2}>
            <Grid size={6}>
              <StaffForm
                roles={personRoles ?? []}
                rolesLoading={loading}
                initialValues={{
                  firstName: person?.firstName ?? '',
                  middleName: person?.middleName,
                  lastName: person?.lastName ?? '',
                  email: person?.email ?? '',
                  jobTitle: person?.jobTitle,
                  roleType: person?.role?.normalizedName as "ADMIN" | "STANDARD" | "OFFICE ADMIN" | "SUPER ADMIN" | "SUPPORT ADMIN" | null
                }}
                readonly={person?.expired
                  || (roles.includes("SUPPORT ADMIN") || roles.includes("STANDARD"))
                }
                onSubmit={handleEditPersonSubmission}
                onCancel={onBack}
              />
            </Grid>
            <Grid size={6}>
              <Grid container spacing={1}>
                <Grid size={12} sx={{ paddingTop: "32px" }}>
                  {/* {(!person?.expired || roles.includes("SUPPORT ADMIN")) && (
                    <PersonTableLinkPreview
                      selectedPerson={person}
                      removeRowId={removeRowId}
                      onLink={onLink}
                      onRemove={handleUnassignment}
                      onDisabled={(reason) => {
                        openModal({ type: reason })
                      }} />
                  )} */}
                  {(person?.role?.normalizedName !== "SUPPORT ADMIN" && !person?.expired) && (
                    <PersonTableLinkPreview
                      selectedPerson={person}
                      removeRowId={removeRowId}
                      onLink={onLink}
                      onRemove={handleUnassignment}
                      onDisabled={(reason) => {
                        openModal({ type: reason })
                      }}
                    />
                  )}
                </Grid>
                <Grid size={12}>
                  <StaffLocationTable
                    selectedPerson={person}
                  />
                </Grid>
              </Grid>
            </Grid>


          </Grid>
        </Grid>
      </Grid>

    </Card>
  );
}
